import { Component, Input, LOCALE_ID, Inject, OnDestroy, OnInit } from '@angular/core';
import { DecimalPipe, formatDate } from "@angular/common";
import { DialogService } from 'primeng/dynamicdialog';
import moment from 'moment';
import { interval, Subscription } from 'rxjs';
import { ChallengeHistoryRanking, ChallengeInvitation, ChallengeStatus, CurrentCardChallenge, CurrentChallenge, Invitation, RankingItem } from '../challenge.model';
import { CxoneChallengeParticipantsModalComponent } from '../cxone-challenge-participants-modal/cxone-challenge-participants-modal.component';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ChallengesService } from '../challenges.service';
import { SolToastrService } from '@niceltd/sol/toastr';
import { CvCustomDialogService } from 'src/app/reusables/custom-dialog/cv-custom-dialog.service';
import { NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CxoneChallengeLeaderboardModalComponent } from '../cxone-challenge-leaderboard-modal/cxone-challenge-leaderboard-modal/cxone-challenge-leaderboard-modal.component';

@Component({
  selector: 'cxone-challenge-card',
  templateUrl: './cxone-challenge-card.component.html',
  styleUrl: './cxone-challenge-card.component.scss',
  providers: [DialogService]
})
export class CxoneChallengeCardComponent implements OnInit, OnDestroy{

    DateFormat = "MMM d, y";
    myRanking: RankingItem | ChallengeHistoryRanking = null;
    localizationStrings: Map<string, string> = new Map<string, string>();
    counterSubscription: Subscription;
    expiresIn: string = "";
    modalOptions: NgbModalOptions = {
        keyboard: false,
        backdrop: "static",
        centered: true,
    };
    currentUser: string = null;

    @Input() challenge: CurrentCardChallenge;
    @Input() challengeStatus: ChallengeStatus;

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private decimalPipe: DecimalPipe,
        private dialogSrc: DialogService,
        private cvLocalizationService: CVLocalizationService,
        private challengeService: ChallengesService,
        private toastManager: SolToastrService,
        private dialogService: CvCustomDialogService,
    ){}

    async ngOnInit() {
        await this.loadLocalization();
        await this.loadCurrentUser();
        if (this.challengeStatus === "pending") {
            this.createCounter(this.getDate('end'));
        }
    }

    ngOnDestroy() {
        if (this.counterSubscription) {
            this.counterSubscription.unsubscribe();
        }
    }

    async loadCurrentUser(): Promise<void> {
        this.currentUser = await this.challengeService.getCurrentUser() as string;
    }

    async loadLocalization() {
        this.localizationStrings = await this.cvLocalizationService.GetSection("Challenges");
    }

    getLocalizationString(key: string, defaultValue: string): string {
        return this.localizationStrings.has(key) ? this.localizationStrings.get(key) : defaultValue;
    }

    getFormattedDate(date: string): string {
        return formatDate(date, this.DateFormat, this.locale);
    }

    getFormattedNumber(value: number): string {
        return this.decimalPipe.transform(value, undefined, CVCultureName);
    }

    getStatusLabel(): string {
        switch(this.challengeStatus) {
            case 'ongoing':
                return this.getLocalizationString("Ongoing", "Ongoing");
            case 'upcoming':
                return this.getLocalizationString("Upcoming", "Upcoming");
            case 'ended':
                return this.getLocalizationString("Ended", "Ended");
            default:
                return this.getLocalizationString("Ongoing", "Ongoing");
        }
    }

    isPendingOngoing() {
        if (this.challengeStatus === "pending") {
            let isOngoing;
            if (this.challenge.StartDate_ForView === this.challenge.EndDate_ForView) {
                isOngoing = moment().isSame(this.challenge.StartDate_ForView, 'day');
            } else {
                isOngoing = moment().isBetween(this.challenge.StartDate_ForView, this.challenge.EndDate_ForView);
            }
            return isOngoing;
        }
        return true;
    }

    getPendingLabel(): string {
        if (this.challengeStatus === "pending") {
            return this.isPendingOngoing() ? this.getLocalizationString("Ongoing", "Ongoing") : this.getLocalizationString("Upcoming", "Upcoming");
        }
        return "";
    }

    getDate(type: "start" | "end"): string {
        if (this.challengeStatus === "pending") {
            return type === 'start' ? this.challenge.StartDate_ForView : this.challenge.EndDate_ForView;
        } else {
            let date = type === 'start' ? (this.challenge as CurrentChallenge).StartDate : (this.challenge as CurrentChallenge).EndDate;
            return moment(date).format('M/D/YYYY');
        }
    }

    getScore() {
        if (!this.myRanking) {
            this.myRanking = (this.challenge as CurrentChallenge).Rankings.find(ranking => ranking.Name === this.getLocalizationString("Me", "Me") || ranking.Name === this.currentUser);
        }
        let score = this.challengeStatus !== "ended" ? this.myRanking?.ViewScore ?? "--" : (this.challenge as any)?.MyDisplayValue ?? "--";
        const leadingScore = this.challengeStatus !== "ended" ? (this.challenge as CurrentChallenge)?.Rankings[0]?.ViewScore ?? "--" : (this.challenge as any)?.WinnerDisplayValue ?? "--"

        //check if the score is 0 when challenge is ended
        if (this.challengeStatus === "ended" && (score === "0" || score === "00:00:00" || score === "0%")) {
            score = "--";
        }
        return {
            score: score ? score : "--",
            leading: leadingScore ? leadingScore : "--",
        };
    }

    openParticipantsModal() {
        this.dialogSrc.open(CxoneChallengeParticipantsModalComponent, {
            width: "700px",
            height: "500px",
            baseZIndex: 10000,
            styleClass: 'cx-one-ui challenge-participants-modal btn-bg-is-white',
            data: {
                participants: this.challengeStatus !== 'ended' ? (this.challenge as any).Details.Participants : [],
                participantsRanking: (this.challenge as CurrentChallenge).Rankings,
                pendingParticipants: (this.challenge as any).NotRankings ?? [],
                headerTitle: this.getLocalizationString("Participant_Status_Title", "Participant Status List"),
                saveLabel: this.getLocalizationString("Close", "Close"),
                getLocalizationString: this.getLocalizationString.bind(this),
                challengeStatus: this.challengeStatus,
                creatorName: (this.challenge as CurrentChallenge).CreatorName ?? (this.challenge as ChallengeInvitation).InvitedBy,
            }
        });
    }

    getFormattedParticipantCount(): string {
        const numberOfParticipants = this.challenge.Details.ParticipantCount;
        const participantLabel = numberOfParticipants > 1 ? this.getLocalizationString("Participants", "Participants") : this.getLocalizationString("Participant", "Participant");
        return `${this.getFormattedNumber(numberOfParticipants)} ${participantLabel.toLocaleLowerCase()}`;
    }

    openLeaderboardModal() {
        this.dialogSrc.open(CxoneChallengeLeaderboardModalComponent, {
            width: "700px",
            height: "500px",
            baseZIndex: 10000,
            styleClass: 'cx-one-ui leaderboard-modal btn-bg-is-white',
            data: {
                rankings: (this.challenge as CurrentChallenge).Rankings,
                headerTitle: this.getLocalizationString("Leaderboard_Title", "Leaderboard"),
                closeLabel: this.getLocalizationString("Close", "Close"),
                getLocalizationString: this.getLocalizationString.bind(this),
                metricName: this.challenge.Metrics
            }
        });
    }

    createCounter(endDate: string) {
        const now = moment();
        const end = moment(endDate).endOf('day');
        const initialDuration = moment.duration(end.diff(now));
        const totalInitialDays = initialDuration.asDays();

        if (totalInitialDays < 1) {
            this.counterSubscription = interval(1000).subscribe(() => {
                const now = moment();
                const duration = moment.duration(end.diff(now));
                const hours = Math.floor(duration.asHours());
                const minutes = duration.minutes();
                const seconds = duration.seconds();
                this.expiresIn = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            });
        } else {
            const years = initialDuration.years();
            const months = initialDuration.months();
            const days = initialDuration.days();

            if (years > 0) {
                const plural = years > 1 ? 's' : '';
                this.expiresIn = `${years} ${this.getLocalizationString("Year"+plural, "year"+plural)}`;
            } else if (months > 0) {
                const plural = months > 1 ? 's' : '';
                this.expiresIn = `${months} ${this.getLocalizationString("Month"+plural, "month"+plural)}`;
            } else {
                const plural = days > 1 ? 's' : '';
                this.expiresIn = `${days} ${this.getLocalizationString("Day"+plural, "day"+plural)}`;
            }
        }
    }

    declineChallenge() {
        const invitation: Invitation = {
            challengeID: this.challenge.ChallengeID,
            invitedBy: (this.challenge as ChallengeInvitation).InvitedByLoginID,
        };
        this.challengeService.declineInvite(invitation).then(() => {
            this.challengeService.removeChallenge(this.challenge.ChallengeID, this.challengeStatus);
            this.toastManager.showToaster(
                "success",
                this.cvLocalizationService.getCachedText(
                    "Challenges",
                    "Challenge_Declined",
                    "Challenge declined successfully."
                )
            );
        });
    }

    acceptChallenge() {
        const invitation: Invitation = {
            challengeID: this.challenge.ChallengeID,
            invitedBy: (this.challenge as ChallengeInvitation).InvitedByLoginID,
        };
        const modalTitle = this.cvLocalizationService.getCachedText(
            "Challenges",
            "Accept_Challenge",
            "Accept Challenge"
        );
        const prompt = this.cvLocalizationService
            .getCachedText(
                "Challenges",
                "Accept_Challenge_Prompt",
                "You will be wagering XX of your XXX coins to accept this challenge"
            )
            .replace("XX", this.getFormattedNumber(this.challenge.Wager))
            .replace("XXX", this.getFormattedNumber(this.challengeService.availableCoins));
        const cancelLabel = this.cvLocalizationService.getCachedText(
            "Common",
            "Cancel_Button",
            "Cancel"
        );
        const okLabel = this.cvLocalizationService.getCachedText(
            "Challenges",
            "Join_Button",
            "Join"
        );
        this.dialogService.showDialog(
            modalTitle,
            prompt,
            4,
            cancelLabel,
            okLabel,
            true,
            () =>
                this.challengeService.acceptInvite(invitation).then((challenge) => {
                    this.challengeService.addChallenge(challenge);
                    this.challengeService.removeChallenge(this.challenge.ChallengeID, this.challengeStatus);
                    this.challengeService.updateUserCoins(-this.challenge.Wager);
                    this.toastManager.showToaster(
                        "success",
                        this.cvLocalizationService.getCachedText(
                            "Challenges",
                            "Challenge_Accepted",
                            "Challenge accepted successfully."
                        )
                    );
                }),
            () => {},
            `accept-invitation-dialog ${this.challengeService.availableCoins < this.challenge.Wager  ? "no-wager" : ""}`,
            true,
            this.modalOptions
        );
    }
}
